import * as Sentry from "@sentry/react";
import classNames from "classnames";
import { PreviewModal } from "components/ChatPreviewModal/ChatPreviewModal";
import { FILE_VIEWED } from "constants/analyticsEvents";
import { CSV, DOC, EXCEL_EXTENSIONS } from "constants/fileTypes";
import {
  DOCUMENT_PREVIEW_STATUS_READY,
  DocumentPreviewStatus,
  GENERATING_PREVIEW_STATUSES,
  WORD_PREVIEW_POLL_INTERVAL_MS,
  WORD_PREVIEW_POLL_TIMEOUT_MS,
} from "constants/documentPreviewStatus";
import { useAnalytics } from "hooks/useAnalytics";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useIsSmallScreen } from "hooks/useIsSmallScreen";
import { PdfViewer } from "components/PdfViewer/PdfViewer";
import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import {
  Route,
  Switch,
  useHistory,
  useLocation,
  useParams,
  useRouteMatch,
} from "react-router-dom";
import PreviewImageName from "static/images/PreviewImageName.svg";
import {
  useGetFolderOrFileQuery,
  useGetPreviewUrlQuery,
} from "store/apis/previewUrl";
import { RootState } from "store/store";
import { ModalProps } from "types/utils/modal";
import { openDownloadUrl } from "util/openDownloadUrl";
import { fileTypeFromName } from "util/fileTypeFromName";
import { NonPreviewableFile } from "components/NonPreviewableFile/NonPreviewableFile";
import { CenteredLoader } from "components/DesignSystem/Loader/loader";
import Async from "./DesignSystem/AsyncComponents/Async";
import { SwitchCase } from "./DesignSystem/SwitchCase/SwitchCase";
import { Button } from "./DesignSystem/Button/Button";
import { DownloadIcon } from "./icons/Download";
import { Refresh } from "./icons/Refresh";

const SpreadsheetPreview = lazy(
  () => import("components/SpreadsheetPreview/SpreadsheetPreview")
);

const ROTATION_CLASSES: Record<number, string> = {
  0: "t-rotate-0",
  90: "t-rotate-90",
  180: "t-rotate-180",
  270: "-t-rotate-90",
};

const ImagePreview = ({
  name,
  preview_url,
  rotation = 0,
}: {
  name: string;
  preview_url: string;
  rotation?: number;
}) => {
  return (
    <div className="t-flex t-items-center t-justify-center t-w-full t-h-full t-overflow-hidden">
      <img
        src={preview_url}
        className={classNames(
          "t-object-contain t-transition-transform t-duration-300",
          ROTATION_CLASSES[rotation],
          rotation === 90 || rotation === 270
            ? "t-max-h-[80vw] t-max-w-[80vh]"
            : "t-max-h-[80vh] t-max-w-[80vw]"
        )}
        alt={name}
      />
    </div>
  );
};

export const PdfPreview = ({
  preview_url,
  name,
}: {
  preview_url: string;
  name: string;
}) => {
  const isSmallScreen = useIsSmallScreen();
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);

  useEffect(() => {
    const container = containerRef.current;

    if (!container) return;

    const observer = new ResizeObserver(([entry]) =>
      setContainerWidth(entry.contentRect.width)
    );

    observer.observe(container);

    return () => observer.disconnect();
  }, [isSmallScreen]);

  // iOS renders a PDF iframe as a single page that cannot be scrolled, so on
  // mobile the pages are rendered directly instead
  if (isSmallScreen) {
    return (
      <div ref={containerRef} className="t-h-full t-w-full">
        {containerWidth > 0 && (
          <PdfViewer
            url={preview_url}
            containerWidth={containerWidth}
            fitContent
          />
        )}
      </div>
    );
  }

  return (
    <iframe className="t-w-full t-h-[96%]" src={preview_url} title={name} />
  );
};

const WordPreview = ({
  name,
  preview_url,
  previewStatus,
  hasPollTimedOut,
}: {
  name: string;
  preview_url: string;
  previewStatus: DocumentPreviewStatus | null | undefined;
  hasPollTimedOut: boolean;
}) => {
  if (previewStatus === DOCUMENT_PREVIEW_STATUS_READY) {
    return <PdfPreview name={name} preview_url={preview_url} />;
  }

  const isGenerating =
    !hasPollTimedOut &&
    GENERATING_PREVIEW_STATUSES.has(previewStatus as DocumentPreviewStatus);
  if (isGenerating) {
    return <CenteredLoader />;
  }

  return <NonPreviewableFile name={name} preview_url={preview_url} />;
};

export const PreviewBody = ({
  file,
  preview_url,
  fullWidth = false,
  rotation = 0,
  hasPreviewPollTimedOut = false,
}: {
  file: any;
  preview_url: string;
  fullWidth?: boolean;
  rotation?: number;
  hasPreviewPollTimedOut?: boolean;
}) => {
  if (preview_url) {
    const spreadsheetPreview = (
      <Sentry.ErrorBoundary
        fallback={
          <NonPreviewableFile
            name={file.name}
            preview_url={preview_url}
            fullWidth={fullWidth}
          />
        }
      >
        <Suspense fallback={<CenteredLoader />}>
          <SpreadsheetPreview
            key={preview_url}
            preview_url={preview_url}
            name={file.name}
            file_size={file.file_size}
            fullWidth={fullWidth}
          />
        </Suspense>
      </Sentry.ErrorBoundary>
    );

    return (
      <SwitchCase
        value={file.file_type}
        cases={{
          IMAGE: (
            <ImagePreview
              name={file.name}
              preview_url={preview_url}
              rotation={rotation}
            />
          ),
          PDF: <PdfPreview name={file.name} preview_url={preview_url} />,
          [DOC]: (
            <WordPreview
              name={file.name}
              preview_url={preview_url}
              previewStatus={file.preview_status}
              hasPollTimedOut={hasPreviewPollTimedOut}
            />
          ),
          EXCEL: spreadsheetPreview,
          [CSV]: spreadsheetPreview,
        }}
        default={
          <NonPreviewableFile
            name={file.name}
            preview_url={preview_url}
            fullWidth={fullWidth}
          />
        }
      />
    );
  }
  return null;
};

export const Preview = ({
  showModal,
  closeModal,
  groupId: groupIdFromProps,
  previewId: previewIdFromProps,
}: {
  showModal: boolean;
  closeModal: () => void;
  groupId?: string;
  previewId?: string | null;
}) => {
  const { trackEvent } = useAnalytics();
  const { previewId } = useParams<{ previewId: string }>();
  const { uuid } = useCurrentGroupContext();
  const { activeChannelGroupId } = useSelector(
    (state: RootState) => state.reviewAndBalancePayment
  );
  const groupId = (groupIdFromProps as string) || uuid || activeChannelGroupId;
  const docId = previewIdFromProps || previewId;

  const [rotation, setRotation] = useState(0);
  const rotate = () => setRotation((prev) => (prev + 90) % 360);

  const [isGeneratingPreview, setIsGeneratingPreview] = useState(false);
  const [hasPollTimedOut, setHasPollTimedOut] = useState(false);

  const { data: file, ...fileApiState } = useGetFolderOrFileQuery(
    { groupId: groupId, fileId: docId },
    {
      skip: !Boolean(docId) || !groupId || !showModal,
      pollingInterval:
        showModal && isGeneratingPreview && !hasPollTimedOut
          ? WORD_PREVIEW_POLL_INTERVAL_MS
          : 0,
    }
  );

  useEffect(() => {
    if (!showModal) {
      setIsGeneratingPreview(false);
      return;
    }
    const isWordFileGenerating =
      file?.file_type === DOC &&
      GENERATING_PREVIEW_STATUSES.has(
        file?.preview_status as DocumentPreviewStatus
      );
    setIsGeneratingPreview(isWordFileGenerating);
  }, [showModal, docId, file?.file_type, file?.preview_status]);

  useEffect(() => {
    if (!isGeneratingPreview) {
      setHasPollTimedOut(false);
      return;
    }
    const timer = setTimeout(
      () => setHasPollTimedOut(true),
      WORD_PREVIEW_POLL_TIMEOUT_MS
    );
    return () => clearTimeout(timer);
  }, [isGeneratingPreview, docId]);

  const previewStatus = file?.preview_status;
  const isWaitingForWordPreview =
    file?.file_type === DOC &&
    Boolean(previewStatus && GENERATING_PREVIEW_STATUSES.has(previewStatus)) &&
    !hasPollTimedOut;

  const { data, ...previewApiState } = useGetPreviewUrlQuery(
    {
      groupId: groupId,
      fileId: file?.uuid,
    },
    {
      skip: !Boolean(file?.uuid) || !groupId || isWaitingForWordPreview,
      refetchOnMountOrArgChange: true,
    }
  );

  const { preview_url, download_url } = data || {};

  useEffect(() => {
    if (previewApiState.isSuccess) {
      trackEvent(FILE_VIEWED, { file_id: file?.uuid! });
    }
  }, [previewApiState.isSuccess]);

  const isLoading =
    previewApiState.isLoading ||
    fileApiState.isLoading ||
    isWaitingForWordPreview;
  const isSuccess = previewApiState.isSuccess && fileApiState.isSuccess;
  const isEmpty = !data || !file;
  const isError = previewApiState.isError || fileApiState.isError;

  const isImage = file?.file_type === "IMAGE";

  return (
    <PreviewModal
      onClose={closeModal}
      title={
        <>
          <img src={PreviewImageName} alt={file?.name} />
          {file?.name}
          {isImage && (
            <Button customType="ghost" size="small" onClick={rotate}>
              <div className="t-text-neutral-0 group-hover:t-text-neutral-100">
                <Refresh size="16" />
              </div>
            </Button>
          )}
          <Button
            customType="ghost"
            size="small"
            onClick={() => openDownloadUrl(download_url!)}
          >
            <div className="t-text-neutral-0 group-hover:t-text-neutral-100">
              <DownloadIcon color="currentColor" />
            </div>
          </Button>
        </>
      }
      open={showModal}
    >
      <Async.Root
        isLoading={isLoading}
        isSuccess={isSuccess}
        isError={isError}
        isEmpty={isEmpty}
      >
        <Async.Empty>
          <></>
        </Async.Empty>
        <Async.ErrorHandler>
          <div className="t-flex t-items-center t-justify-center t-gap-1 t-rounded-lg t-p-7 t-bg-neutral-80 t-text-white t-h-14 t-self-center t-justify-self-center">
            Some issue opening the document, please contact us if this happens
            in multiple instances
          </div>
        </Async.ErrorHandler>
        <Async.Success>
          <PreviewBody
            file={file}
            preview_url={preview_url!}
            rotation={rotation}
            hasPreviewPollTimedOut={hasPollTimedOut}
          />
        </Async.Success>
      </Async.Root>
    </PreviewModal>
  );
};

export const DocumentPreviewModal = ({
  groupId,
}: {
  groupId?: string;
}): React.JSX.Element | null => {
  const { url } = useRouteMatch();
  const history = useHistory();
  const { search } = useLocation();

  const closeModal = () => {
    history.push(`${url}${search}`);
  };

  return (
    <Switch>
      <Route path={`${url}/preview/:previewId/`}>
        <Preview closeModal={closeModal} groupId={groupId} showModal={true} />
      </Route>
    </Switch>
  );
};

export const LocalFilePreview = ({
  file,
  isOpen,
  close,
}: { file: File } & ModalProps) => {
  const preview_url = URL.createObjectURL(file);

  const isPdf = file.type === "application/pdf";
  const isImage = file.type.startsWith("image/");
  const isExcel = EXCEL_EXTENSIONS.includes(fileTypeFromName(file.name));

  return (
    <PreviewModal
      title={
        <>
          <img src={PreviewImageName} alt={file?.name} />
          {file?.name}
        </>
      }
      open={isOpen}
      onClose={close}
    >
      <SwitchCase
        value="true"
        cases={{
          [String(isPdf)]: (
            <PdfPreview preview_url={preview_url} name={file.name} />
          ),
          [String(isImage)]: (
            <ImagePreview preview_url={preview_url} name={file.name} />
          ),
          [String(isExcel)]: (
            <Suspense fallback={<CenteredLoader />}>
              <SpreadsheetPreview
                preview_url={preview_url}
                name={file.name}
                file_size={file.size}
              />
            </Suspense>
          ),
        }}
        default={
          <NonPreviewableFile name={file.name} preview_url={preview_url} />
        }
      />
    </PreviewModal>
  );
};

export const FilePreviewWithoutGroup = ({
  showModal,
  closeModal,
  previewId: previewIdFromProps,
}: {
  showModal: boolean;
  closeModal: () => void;
  previewId?: string | null;
}) => {
  const { trackEvent } = useAnalytics();
  const { previewId } = useParams<{ previewId: string }>();
  const { uuid } = useCurrentGroupContext();
  const { activeChannelGroupId } = useSelector(
    (state: RootState) => state.reviewAndBalancePayment
  );
  const docId = previewIdFromProps || previewId;

  const [rotation, setRotation] = useState(0);
  const rotate = () => setRotation((prev) => (prev + 90) % 360);

  const [isGeneratingPreview, setIsGeneratingPreview] = useState(false);
  const [hasPollTimedOut, setHasPollTimedOut] = useState(false);

  const { data: file, ...fileApiState } = useGetFolderOrFileQuery(
    { groupId: "", fileId: docId },
    {
      skip: !Boolean(docId) || !showModal,
      pollingInterval:
        showModal && isGeneratingPreview && !hasPollTimedOut
          ? WORD_PREVIEW_POLL_INTERVAL_MS
          : 0,
    }
  );

  useEffect(() => {
    if (!showModal) {
      setIsGeneratingPreview(false);
      return;
    }
    const isWordFileGenerating =
      file?.file_type === DOC &&
      GENERATING_PREVIEW_STATUSES.has(
        file?.preview_status as DocumentPreviewStatus
      );
    setIsGeneratingPreview(isWordFileGenerating);
  }, [showModal, docId, file?.file_type, file?.preview_status]);

  useEffect(() => {
    if (!isGeneratingPreview) {
      setHasPollTimedOut(false);
      return;
    }
    const timer = setTimeout(
      () => setHasPollTimedOut(true),
      WORD_PREVIEW_POLL_TIMEOUT_MS
    );
    return () => clearTimeout(timer);
  }, [isGeneratingPreview, docId]);

  const previewStatus = file?.preview_status;
  const isWaitingForWordPreview =
    file?.file_type === DOC &&
    Boolean(previewStatus && GENERATING_PREVIEW_STATUSES.has(previewStatus)) &&
    !hasPollTimedOut;

  const { data, ...previewApiState } = useGetPreviewUrlQuery(
    {
      groupId: "",
      fileId: file?.uuid,
    },
    {
      skip: !Boolean(file?.uuid) || isWaitingForWordPreview,
      refetchOnMountOrArgChange: true,
    }
  );

  const { preview_url, download_url } = data || {};

  useEffect(() => {
    if (previewApiState.isSuccess) {
      trackEvent(FILE_VIEWED, { file_id: file?.uuid! });
    }
  }, [previewApiState.isSuccess]);

  const isLoading =
    previewApiState.isLoading ||
    fileApiState.isLoading ||
    isWaitingForWordPreview;
  const isSuccess = previewApiState.isSuccess && fileApiState.isSuccess;
  const isEmpty = !data || !file;
  const isError = previewApiState.isError || fileApiState.isError;

  const isImage = file?.file_type === "IMAGE";

  return (
    <PreviewModal
      onClose={closeModal}
      title={
        <>
          <img src={PreviewImageName} alt={file?.name} />
          {file?.name}
          {isImage && (
            <Button customType="ghost" size="small" onClick={rotate}>
              <div className="t-text-neutral-0 group-hover:t-text-neutral-100">
                <Refresh size="16" />
              </div>
            </Button>
          )}
          <Button
            customType="ghost"
            size="small"
            onClick={() => openDownloadUrl(download_url!)}
          >
            <div className="t-text-neutral-0">
              <DownloadIcon color="currentColor" />
            </div>
          </Button>
        </>
      }
      open={showModal}
    >
      <Async.Root
        isLoading={isLoading}
        isSuccess={isSuccess}
        isError={isError}
        isEmpty={isEmpty}
      >
        <Async.Empty>
          <></>
        </Async.Empty>
        <Async.ErrorHandler>
          <div className="t-flex t-items-center t-justify-center t-gap-1 t-rounded-lg t-p-7 t-bg-neutral-80 t-text-white t-h-14 t-self-center t-justify-self-center">
            Some issue opening the document, please contact us if this happens
            in multiple instances
          </div>
        </Async.ErrorHandler>
        <Async.Success>
          <PreviewBody
            file={file}
            preview_url={preview_url!}
            rotation={rotation}
            hasPreviewPollTimedOut={hasPollTimedOut}
          />
        </Async.Success>
      </Async.Root>
    </PreviewModal>
  );
};
